/* eslint-disable react/jsx-wrap-multilines */
import React, { useEffect } from 'react';
import { Routes, Route, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { Provider as AuthProvider } from 'contexts/auth';
import { Provider as AlertProvider } from 'contexts/alert';
import { Provider as CompanyProvider } from 'contexts/company';
import { Provider as LanguagesProvider } from 'contexts/languages';
import { config } from 'codemash';
import {
  RequireAuth,
  Login,
  Logout,
  NotFoundPage,
  OrderDetails,
  Orders,
  Products,
  ProductsAddNew,
  Tables,
  TablesAddNew,
  Employees,
  EmployeesAddNew,
  EmployeesEdit,
  Drivers,
  DriversAddNew,
  DriversEdit,
  HomeScreen,
  Restaurants,
  RestaurantsAddNew,
  RestaurantsEdit,
  ProductsEdit,
  ProductCategories,
  ProductCategoriesAddNew,
  ProductCategoriesEdit,
  Settings,
  VariationTemplates,
  VariationTemplatesAddNew,
  VariationTemplatesEdit,
  DiscountsAndPricing,
  PricingAddNew,
  PricingEdit,
  Menus,
  MenuAddNew,
  MenuEdit,
  Reviews,
  ReviewDetails,
  TableDetails,
  PaymentsBalance,
  AssignTips,
} from 'views';
import { MainLayout } from 'components/layouts';
import { createBrowserHistory } from 'history';
import { Config as ProjectConfig } from 'configs';
import { ToasterMessage } from 'components/alerts/toasterMessage';
import { CompanyEdit } from 'views/company/edit';
import { Profile } from 'views/profile';
import { DeliverySettingsEdit } from 'views/delivery/edit';
import { DeliveryPages } from 'views/delivery';
import { DeliverySettingsAddNew } from 'views/delivery/new';
import { ActionAlert } from 'components/alerts/actionAlert';
import { CouponAddNew } from 'views/discounts-pricing/coupons/new';
import { CouponEdit } from 'views/discounts-pricing/coupons/edit';
import { ProductsImportExport } from 'views/products/imports';
import { ProductsImportExportAddNew } from 'views/products/imports/new';
import { CompanyOnboarding } from 'views/company/onboarding';
import { VariationJoins } from 'views/variation-joins';
import { VariationJoinsAddNew } from 'views/variation-joins/new';
import { VariationJoinsEdit } from 'views/variation-joins/edit';
import { initOneSignal } from 'services/oneSignal';
import { RestaurantEvents } from 'views/additional/events';
import { RestaurantEventsAddNew } from 'views/additional/events/new';
import { RestaurantEventsEdit } from 'views/additional/events/edit';
import { RestaurantPromotions } from 'views/additional/promotions';
import { RestaurantPromotionsAddNew } from 'views/additional/promotions/new';
import { RestaurantPromotionsEdit } from 'views/additional/promotions/edit';
import { ResetPasswordWithToken } from 'views/auth/passwordReset';

config.init(
  {
    secretKey: ProjectConfig.CODEMASH.SECRET_KEY,
    projectId: ProjectConfig.CODEMASH.PROJECT_ID,
  },
  process.env.NODE_ENV,
);

export const App = () => {
  const history = createBrowserHistory();

  useEffect(() => {
    initOneSignal();
  }, []);

  return (
    <AlertProvider>
      <ToasterMessage />
      <ActionAlert />
      <AuthProvider>
        <CompanyProvider>
          <LanguagesProvider>
            <HistoryRouter history={history}>
              <Routes>
                {/* Auth */}
                <Route path="/login" element={<Login />} />
                <Route path="/logout" element={<Logout />} />
                <Route path="/password/reset" element={<ResetPasswordWithToken />} />
                <Route
                  path="/.well-known/apple-app-site-association"
                  element={() => {
                    window.location.reload();
                  }}
                />
                <Route
                  path="/"
                  element={
                    <RequireAuth>
                      <MainLayout />
                    </RequireAuth>
                  }
                >
                  {/* Home screen */}
                  <Route
                    index
                    element={
                      <RequireAuth>
                        <HomeScreen />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/home"
                    element={
                      <RequireAuth>
                        <HomeScreen />
                      </RequireAuth>
                    }
                  />

                  {/* Orders */}
                  <Route
                    path="/orders"
                    element={
                      <RequireAuth>
                        <Orders />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/orders/:id"
                    element={
                      <RequireAuth>
                        <OrderDetails />
                      </RequireAuth>
                    }
                  />
                  {/* Products */}

                  <Route
                    path="/products/imports"
                    element={
                      <RequireAuth>
                        <ProductsImportExport />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/products/imports/add-new"
                    element={
                      <RequireAuth>
                        <ProductsImportExportAddNew />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/products"
                    element={
                      <RequireAuth>
                        <Products />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/products/add-new"
                    element={
                      <RequireAuth>
                        <ProductsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/products/:id"
                    element={
                      <RequireAuth>
                        <ProductsEdit />
                      </RequireAuth>
                    }
                  />

                  {/* Menu */}
                  <Route
                    path="/menu"
                    element={
                      <RequireAuth>
                        <Menus />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/menu/add-new"
                    element={
                      <RequireAuth>
                        <MenuAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/menu/:id"
                    element={
                      <RequireAuth>
                        <MenuEdit />
                      </RequireAuth>
                    }
                  />

                  {/* Reviews */}
                  <Route
                    path="/reviews"
                    element={
                      <RequireAuth>
                        <Reviews />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/reviews/:id"
                    element={
                      <RequireAuth>
                        <ReviewDetails />
                      </RequireAuth>
                    }
                  />

                  {/* Payments */}
                  <Route
                    path="/payments"
                    element={
                      <RequireAuth>
                        <PaymentsBalance />
                      </RequireAuth>
                    }
                  />

                  {/* Assign tips */}
                  <Route
                    path="/assign-tips"
                    element={
                      <RequireAuth>
                        <AssignTips />
                      </RequireAuth>
                    }
                  />

                  {/* Discounts & Pricing */}

                  <Route
                    path="/discounts"
                    element={
                      <RequireAuth>
                        <DiscountsAndPricing />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/discounts/pricing/add-new"
                    element={
                      <RequireAuth>
                        <PricingAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/discounts/pricing/:id"
                    element={
                      <RequireAuth>
                        <PricingEdit />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/discounts/coupons/add-new"
                    element={
                      <RequireAuth>
                        <CouponAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/discounts/coupons/:id"
                    element={
                      <RequireAuth>
                        <CouponEdit />
                      </RequireAuth>
                    }
                  />
                  {/* Settings */}
                  <Route
                    path="/settings"
                    element={
                      <RequireAuth>
                        <Settings />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/tables"
                    element={
                      <RequireAuth>
                        <Tables />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/tables/add-new"
                    element={
                      <RequireAuth>
                        <TablesAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/tables/:id"
                    element={
                      <RequireAuth>
                        <TableDetails />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/employees"
                    element={
                      <RequireAuth>
                        <Employees />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/employees/add-new"
                    element={
                      <RequireAuth>
                        <EmployeesAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/employees/:id"
                    element={
                      <RequireAuth>
                        <EmployeesEdit />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/settings/drivers"
                    element={
                      <RequireAuth>
                        <Drivers />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/drivers/add-new"
                    element={
                      <RequireAuth>
                        <DriversAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/drivers/:id"
                    element={
                      <RequireAuth>
                        <DriversEdit />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/settings/restaurants"
                    element={
                      <RequireAuth>
                        <Restaurants />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/restaurants/add-new"
                    element={
                      <RequireAuth>
                        <RestaurantsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/restaurants/:id"
                    element={
                      <RequireAuth>
                        <RestaurantsEdit />
                      </RequireAuth>
                    }
                  />
                  {/* <Route
                      path="settings/restaurants/:id/working-days"
                      element={
                        <RequireAuth>
                          <RestaurantWorkingDaysEdit />
                        </RequireAuth>
                      }
                    /> */}
                  <Route
                    path="/settings/product-categories"
                    element={
                      <RequireAuth>
                        <ProductCategories />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/product-categories/add-new"
                    element={
                      <RequireAuth>
                        <ProductCategoriesAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/product-categories/:id"
                    element={
                      <RequireAuth>
                        <ProductCategoriesEdit />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-joins"
                    element={
                      <RequireAuth>
                        <VariationJoins />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-joins/add-new"
                    element={
                      <RequireAuth>
                        <VariationJoinsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-joins/:id"
                    element={
                      <RequireAuth>
                        <VariationJoinsEdit />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-templates"
                    element={
                      <RequireAuth>
                        <VariationTemplates />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-templates/add-new"
                    element={
                      <RequireAuth>
                        <VariationTemplatesAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/variation-templates/:id"
                    element={
                      <RequireAuth>
                        <VariationTemplatesEdit />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/delivery"
                    element={
                      <RequireAuth>
                        <DeliveryPages />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/delivery/add-new"
                    element={
                      <RequireAuth>
                        <DeliverySettingsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/settings/delivery/:id"
                    element={
                      <RequireAuth>
                        <DeliverySettingsEdit />
                      </RequireAuth>
                    }
                  />
                  {/* <Route
                    path="/settings/payment-accounts"
                    element={
                      <RequireAuth>
                        <PaymentAccounts />
                      </RequireAuth>
                  }
                  />
                  <Route
                    path="/settings/payment-accounts/add-new"
                    element={
                      <RequireAuth>
                        <PaymentAccountAddNew />
                      </RequireAuth>
                  }
                  />
                  <Route
                    path="/settings/payment-accounts/:id"
                    element={
                      <RequireAuth>
                        <PaymentAccountEdit />
                      </RequireAuth>
                  }
                  /> */}
                  <Route
                    path="/account"
                    element={
                      <RequireAuth>
                        <CompanyEdit />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/onboarding"
                    element={
                      <RequireAuth>
                        <CompanyOnboarding />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/profile"
                    element={
                      <RequireAuth>
                        <Profile />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/additional/events"
                    element={
                      <RequireAuth>
                        <RestaurantEvents />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/additional/events/add-new"
                    element={
                      <RequireAuth>
                        <RestaurantEventsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/additional/events/:id"
                    element={
                      <RequireAuth>
                        <RestaurantEventsEdit />
                      </RequireAuth>
                    }
                  />

                  <Route
                    path="/additional/promotions"
                    element={
                      <RequireAuth>
                        <RestaurantPromotions />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/additional/promotions/add-new"
                    element={
                      <RequireAuth>
                        <RestaurantPromotionsAddNew />
                      </RequireAuth>
                    }
                  />
                  <Route
                    path="/additional/promotions/:id"
                    element={
                      <RequireAuth>
                        <RestaurantPromotionsEdit />
                      </RequireAuth>
                    }
                  />

                  <Route path="*" element={<NotFoundPage />} />
                </Route>
              </Routes>
            </HistoryRouter>
          </LanguagesProvider>
        </CompanyProvider>
      </AuthProvider>
    </AlertProvider>
  );
};
